import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Content from '../common/template/content';
import Row from '../common/layout/row';
import Select from '../common/form/select';
import FormatUtils from '../common/formatUtils/FormatUtils';
import {
    getListaPorCooperativa,
    getListaPorEmpresa,
    getListaPorTurma,
    getListaPorFuncionario,
    getListaRelatorioPresencaPorEmpresa,
    getListaRelatorioPresencaPorCooperativa,
    getListarPorFuncionarioQuantidadeEmpresa
} from './dashboardActions';
import './dashboard.css';

const meses = [
    { id: '01', valor: 'Janeiro' },
    { id: '02', valor: 'Fevereiro' },
    { id: '03', valor: 'Março' },
    { id: '04', valor: 'Abril' },
    { id: '05', valor: 'Maio' },
    { id: '06', valor: 'Junho' },
    { id: '07', valor: 'Julho' },
    { id: '08', valor: 'Agosto' },
    { id: '09', valor: 'Setembro' },
    { id: '10', valor: 'Outubro' },
    { id: '11', valor: 'Novembro' },
    { id: '12', valor: 'Dezembro' }
];

class Dashboard extends Component {
    state = {
        filtro: {
            tipo: 'AMBAS',
            ano: String(new Date().getFullYear())
        }
    };

    componentDidMount() {
        this.atualizar(this.state.filtro);
    }

    atualizar = (filtro = {}) => {
        this.setState({ filtro: { ...filtro } });
        this.props.getListaPorCooperativa();
        this.props.getListaPorTurma();
        this.props.getListaPorEmpresa();
        this.props.getListaPorFuncionario();
        this.props.getListaRelatorioPresencaPorEmpresa({ ...filtro });
        this.props.getListaRelatorioPresencaPorCooperativa({ ...filtro });
        this.props.getListarPorFuncionarioQuantidadeEmpresa({ ...filtro });
    };

    getTotal = lista => {
        if (!lista || !lista.length) return 0;
        return lista.reduce((total, item) => total + Number(item.quantidade || 0), 0);
    };

    formatarNumero = (valor, casasDecimais = 0) =>
        FormatUtils.formatarValorTela(valor || 0, casasDecimais);

    criarUrlGrafico = (labels, dados, cor, rotulo, horizontal = false) => {
        const configuracao = {
            type: horizontal ? 'horizontalBar' : 'bar',
            data: {
                labels,
                datasets: [{
                    label: rotulo,
                    data: dados,
                    backgroundColor: cor,
                    borderWidth: 0,
                    maxBarThickness: 28
                }]
            },
            options: {
                legend: { display: false },
                tooltips: {
                    backgroundColor: '#18372d',
                    titleFontColor: '#ffffff',
                    bodyFontColor: '#ffffff',
                    cornerRadius: 8,
                    xPadding: 12,
                    yPadding: 10
                },
                layout: { padding: { top: 8, right: 10, bottom: 0, left: 2 } },
                scales: {
                    yAxes: [{
                        ticks: { beginAtZero: true, fontColor: '#62716b', fontSize: 10 },
                        gridLines: { display: !horizontal, color: '#edf2ef', zeroLineColor: '#dfe8e4' }
                    }],
                    xAxes: [{
                        ticks: { beginAtZero: true, fontColor: '#62716b', fontSize: 9, autoSkip: false, maxRotation: 25, minRotation: 0 },
                        gridLines: { display: horizontal, color: '#edf2ef', zeroLineColor: '#dfe8e4' }
                    }]
                }
            }
        };

        return `https://quickchart.io/chart?width=900&height=300&devicePixelRatio=2&c=${encodeURIComponent(JSON.stringify(configuracao))}`;
    };

    renderMetric = (label, value, icon, className) => (
        <article className={`dashboard-metric ${className}`}>
            <div className="dashboard-metric-icon" aria-hidden="true"><i className={icon} /></div>
            <div>
                <strong className="dashboard-metric-value">{this.formatarNumero(value)}</strong>
                <span className="dashboard-metric-label">{label}</span>
            </div>
        </article>
    );

    renderRanking = (lista, campoNome, campoValor = 'participacao', sufixo = '%') => {
        if (!lista || !lista.length) {
            return <div className="dashboard-empty">Nenhum dado disponível para este período.</div>;
        }

        const itens = lista.slice().sort((a, b) => Number(b[campoValor] || 0) - Number(a[campoValor] || 0));
        const maiorValor = Math.max(...itens.map(item => Number(item[campoValor] || 0)), 1);

        return (
            <div className="dashboard-ranking">
                {itens.map((item, index) => {
                    const valor = Number(item[campoValor] || 0);
                    const largura = campoValor === 'participacao'
                        ? Math.max(0, Math.min(valor, 100))
                        : Math.max(0, Math.min((valor / maiorValor) * 100, 100));
                    return (
                        <div className="dashboard-ranking-row" key={`${item[campoNome] || 'item'}-${index}`}>
                            <span className="dashboard-ranking-name" title={item[campoNome]}>{item[campoNome]}</span>
                            <div className="dashboard-ranking-track" aria-hidden="true">
                                <div className="dashboard-ranking-fill" style={{ width: `${largura}%` }} />
                            </div>
                            <span className="dashboard-ranking-value">
                                {this.formatarNumero(valor, campoValor === 'participacao' ? 2 : 0)}{sufixo}
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    };

    renderPanel = (title, subtitle, icon, content, wide = false) => (
        <section className={`dashboard-panel${wide ? ' dashboard-panel--wide' : ''}`}>
            <header className="dashboard-panel-header">
                <div className="dashboard-panel-title">
                    <i className={icon} aria-hidden="true" />
                    <div><h3>{title}</h3><span>{subtitle}</span></div>
                </div>
            </header>
            <div className="dashboard-panel-body">{content}</div>
        </section>
    );

    render() {
        const anoAtual = new Date().getFullYear();
        const anos = Array.from({ length: anoAtual - 2022 + 1 }, (_, index) => {
            const ano = String(2022 + index);
            return { id: ano, valor: ano };
        });
        const mesSelecionado = meses.find(item => item.id === this.state.filtro.mes);
        const nomeMes = mesSelecionado ? mesSelecionado.valor : 'Todos os meses';
        const cooperativas = this.props.listaRelatorioPresencaPorCooperativa || [];
        const empresas = this.props.listaRelatorioPresencaPorEmpresa || [];
        const funcionariosEmpresa = this.props.listaPorFuncionarioQuantidadeEmpresa || [];

        return (
            <Content>
                <main className="seft-dashboard">
                    <section className="dashboard-hero">
                        <div>
                            <span className="dashboard-eyebrow">Visão geral</span>
                            <h1>Painel de indicadores</h1>
                            <p>Acompanhe a participação dos funcionários</p>
                        </div>
                        <div className="dashboard-period">
                            <span>Período selecionado</span>
                            <strong>{nomeMes} de {this.state.filtro.ano}</strong>
                        </div>
                    </section>

                    <section className="dashboard-filter-card" aria-label="Filtros do painel">
                        <div className="dashboard-filter-title"><i className="fas fa-sliders-h" aria-hidden="true" />Filtros de visualização</div>
                        <Row>
                            <Select
                                options={[{ id: 'AMBAS', valor: 'Ambas' }, { id: 'INDIVIDUAL', valor: 'Individual' }, { id: 'COLETIVA', valor: 'Coletiva' }]}
                                label="Tipo de participação"
                                cols="12 4 4 4"
                                placeholder="Selecione"
                                value={this.state.filtro.tipo}
                                onChange={value => this.atualizar({ ...this.state.filtro, tipo: value })}
                            />
                            <Select options={anos} label="Ano" cols="12 4 4 4" placeholder="Selecione" value={this.state.filtro.ano} onChange={value => this.atualizar({ ...this.state.filtro, ano: value })} />
                            <Select options={meses} label="Mês" cols="12 4 4 4" placeholder="Selecione" value={this.state.filtro.mes} onChange={value => this.atualizar({ ...this.state.filtro, mes: value })} />
                        </Row>
                    </section>

                    <section className="dashboard-metrics" aria-label="Indicadores principais">
                        {this.renderMetric('Cooperativas', this.getTotal(this.props.listaPorCooperativa), 'fas fa-handshake', 'dashboard-metric--cooperativas')}
                        {this.renderMetric('Empresas', this.getTotal(this.props.listaPorEmpresa), 'fas fa-building', 'dashboard-metric--empresas')}
                        {this.renderMetric('Turmas', this.getTotal(this.props.listaPorTurma), 'fas fa-users', 'dashboard-metric--turmas')}
                        {this.renderMetric('Funcionários', this.getTotal(this.props.listaPorFuncionario), 'fas fa-user-tie', 'dashboard-metric--funcionarios')}
                    </section>

                    <div className="dashboard-reports">
                        {this.renderPanel('Participação por cooperativa', `${cooperativas.length} ${cooperativas.length === 1 ? 'cooperativa' : 'cooperativas'}`, 'fas fa-handshake', this.renderRanking(cooperativas, 'nome_cooperativa'), true)}
                        {this.renderPanel('Participação por empresa', `${empresas.length} ${empresas.length === 1 ? 'empresa' : 'empresas'}`, 'fas fa-building', this.renderRanking(empresas, 'nome_empresa'), true)}
                        {this.renderPanel('Funcionários por empresa', `${funcionariosEmpresa.length} ${funcionariosEmpresa.length === 1 ? 'empresa' : 'empresas'}`, 'fas fa-user-tie', this.renderRanking(funcionariosEmpresa, 'nome_empresa', 'quantidade', ''), true)}
                    </div>
                </main>
            </Content>
        );
    }
}

const mapStateToProps = state => ({
    sessao: state.auth.usuario,
    listaPorCooperativa: state.dashboard.listaPorCooperativa,
    listaPorFuncionario: state.dashboard.listaPorFuncionario,
    listaPorTurma: state.dashboard.listaPorTurma,
    listaPorEmpresa: state.dashboard.listaPorEmpresa,
    listaRelatorioPresencaPorEmpresa: state.dashboard.listaRelatorioPresencaPorEmpresa,
    listaRelatorioPresencaPorCooperativa: state.dashboard.listaRelatorioPresencaPorCooperativa,
    listaCores: state.dashboard.listaCores,
    listaPorFuncionarioQuantidadeEmpresa: state.dashboard.listaPorFuncionarioQuantidadeEmpresa
});

const mapDispatchToProps = dispatch => bindActionCreators({
    getListaPorCooperativa,
    getListaPorEmpresa,
    getListaPorTurma,
    getListaPorFuncionario,
    getListaRelatorioPresencaPorEmpresa,
    getListaRelatorioPresencaPorCooperativa,
    getListarPorFuncionarioQuantidadeEmpresa
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
